import { Link } from "react-router-dom";
import { Phone, MessageSquareText, MapPin, Clock } from "lucide-react";
import { LogoImage } from "./Logo";
import { PHONE_TEL, PHONE_SMS, PHONE_DISPLAY, AREAS } from "../site";
import { SERVICES } from "../data/services";

const SKYLINE =
  "https://images.pexels.com/photos/39613830/pexels-photo-39613830.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const COMPANY_LINKS = [
  { label: "About us", to: "/about" },
  { label: "Contact", to: "/contact" },
  { label: "Privacy policy", to: "/privacy" },
];

export const Footer = () => (
  <footer data-testid="site-footer" className="relative bg-zinc-950 text-white overflow-hidden">
    <div className="relative h-56 md:h-72 overflow-hidden">
      <img
        src={SKYLINE}
        alt="Winnipeg skyline and bridge at dusk"
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover opacity-60"
        data-testid="footer-skyline-image"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#FAFAFA] via-transparent to-zinc-950" aria-hidden="true" />
    </div>

    <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-12 -mt-10">
      <div className="grid md:grid-cols-12 gap-10 md:gap-8">
        <div className="md:col-span-4">
          <div className="mb-5">
            <LogoImage variant="light" className="h-10 w-auto" />
          </div>
          <p className="text-sm leading-relaxed text-zinc-400 max-w-sm">
            Winnipeg's automotive and residential locksmith. Car and home
            lockouts, key fobs and transponder keys — fixed on the spot, around
            the clock.
          </p>
        </div>

        <div className="md:col-span-3">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-5">Reach us</p>
          <div className="space-y-3">
            <a
              href={PHONE_TEL}
              data-testid="footer-call-link"
              className="flex items-center gap-3 text-sm text-zinc-300 hover:text-white transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
            >
              <Phone className="h-4 w-4 text-brand" /> {PHONE_DISPLAY}
            </a>
            <a
              href={PHONE_SMS}
              data-testid="footer-text-link"
              className="flex items-center gap-3 text-sm text-zinc-300 hover:text-white transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
            >
              <MessageSquareText className="h-4 w-4 text-brand" /> Text us anytime
            </a>
            <p className="flex items-center gap-3 text-sm text-zinc-300">
              <Clock className="h-4 w-4 text-brand" /> Open 24 hours, 7 days
            </p>
            <p className="flex items-center gap-3 text-sm text-zinc-300">
              <MapPin className="h-4 w-4 text-brand" /> Winnipeg, Manitoba
            </p>
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-5">Services</p>
          <ul className="space-y-2.5">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`footer-service-${s.slug}`}
                  className="text-sm text-zinc-300 hover:text-white transition-colors duration-200"
                >
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-5">Company</p>
          <ul className="space-y-2.5">
            {COMPANY_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="text-sm text-zinc-300 hover:text-white transition-colors duration-200"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="mt-12 text-xs leading-loose text-zinc-500" data-testid="footer-areas-list">
        Serving {AREAS.join(" · ")} and surrounding Manitoba communities.
      </p>

      <div className="mt-6 pt-8 border-t border-zinc-800 flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs text-zinc-500">© {new Date().getFullYear()} Arrow Locksmith. All rights reserved.</p>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600">Made for the Peg</p>
      </div>
    </div>
  </footer>
);
